import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CloudIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ServerStackIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import * as api from '../services/api';

interface VaultSecurityPosture {
  vaultName: string;
  softDeleteEnabled: boolean;
  immutabilityEnabled: boolean;
  enhancedSecurityEnabled: boolean;
  crossRegionRestoreEnabled: boolean;
  geoRedundant: boolean;
  customerManagedKey: boolean;
  publicNetworkDisabled: boolean;
  issues: string[];
}

interface BackupVault {
  id: string;
  name: string;
  location: string;
  resourceGroup: string;
  type: 'RecoveryServices' | 'DataProtection';
}

interface ProtectedItem {
  id: string;
  name: string;
  resourceType: string;
  policyName: string;
  lastBackupTime: string | null;
  lastRecoveryPoint: string | null;
  lastBackupStatus: string | null;
  protectionState: string;
  healthStatus: string;
  vaultName: string;
}

interface ScoringBreakdown {
  noVault: number;
  noProtectedItems: number;
  unprotectedResources: number;
  failedBackups: number;
  staleBackups: number;
  unhealthyItems: number;
  softDeleteDisabled: number;
  noImmutability: number;
  noGeoRedundancy: number;
  noRestoreTests: number;
  publicNetworkAccess: number;
  totalDeductions: number;
}

interface AuditResult {
  nodeId: string;
  name: string;
  status: 'pass' | 'warn' | 'fail' | 'error' | 'not-assessed';
  score: number;
  findings: number;
  details: {
    subscriptionId: string;
    vaults: BackupVault[];
    protectedItems: ProtectedItem[];
    unprotectedVMs: Array<{ id: string; name: string }>;
    auditGaps: Array<{ scope: 'subscription' | 'vault'; resource: string; reason: string }>;
    excluded: Array<{
      id: string;
      resourceKey: string;
      resourceType: 'unprotected_vm' | 'protected_item';
      resourceName: string;
      justification: string;
      createdBy: string | null;
      createdAt: string;
    }>;
    staleBackups: ProtectedItem[];
    vaultSecurity: VaultSecurityPosture[];
    restoreTestsFound: boolean;
    recentRestoreJobCount: number;
    summary: string;
    scoringBreakdown: ScoringBreakdown;
  };
  timestamp: string;
}

interface ConfigStatus {
  configured: boolean;
  hasSubscription: boolean;
  hasConsent: boolean;
  subscriptionId: string | null;
}

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  pass: { bg: 'bg-green-400/10', text: 'text-green-400', dot: 'bg-green-400' },
  warn: { bg: 'bg-amber-400/10', text: 'text-amber-400', dot: 'bg-amber-400' },
  fail: { bg: 'bg-red-400/10', text: 'text-red-400', dot: 'bg-red-400' },
  error: { bg: 'bg-red-400/10', text: 'text-red-400', dot: 'bg-red-400' },
  'not-assessed': { bg: 'bg-white/5', text: 'text-white/40', dot: 'bg-white/30' },
};

const SecurityBadge: React.FC<{ enabled: boolean; label: string }> = ({ enabled, label }) => (
  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${
    enabled ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'
  }`}>
    {enabled
      ? <CheckCircleIcon className="h-3.5 w-3.5" />
      : <XCircleIcon className="h-3.5 w-3.5" />
    }
    {label}
  </div>
);

const ScoreRing: React.FC<{ score: number; status: string; size?: number }> = ({ score, status, size = 120 }) => {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const colors = statusColors[status] || statusColors['not-assessed'];

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="currentColor" strokeWidth="6" className="text-white/[0.06]" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" strokeWidth="6" strokeLinecap="round"
          stroke="currentColor" className={colors.text}
          strokeDasharray={circumference} strokeDashoffset={circumference - progress}
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-2xl font-bold ${colors.text}`}>{score}</span>
        <span className="text-[10px] text-anchor-muted uppercase tracking-wider">Score</span>
      </div>
    </div>
  );
};

const NotConfigured: React.FC<{ onConnect: () => void; connecting: boolean }> = ({ onConnect, connecting }) => (
  <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-12 text-center">
    <CloudIcon className="h-16 w-16 text-white/20 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-white mb-2">Azure Backup Not Configured</h3>
    <p className="text-sm text-anchor-muted max-w-md mx-auto mb-6">
      Connect your Azure subscription to audit Recovery Services Vaults, Backup Center,
      and protected resources against CIS v8 Control 11.
    </p>
    <button
      onClick={onConnect}
      disabled={connecting}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-anchor-blue hover:bg-anchor-blue/80 transition-colors disabled:opacity-50"
    >
      <LinkIcon className={`h-4 w-4 ${connecting ? 'animate-pulse' : ''}`} />
      {connecting ? 'Redirecting...' : 'Connect Azure'}
    </button>
    <p className="text-xs text-anchor-muted mt-4 max-w-sm mx-auto">
      Sign in as an Azure administrator — ANCHOR sets up read-only access (Reader role) automatically.
    </p>
  </div>
);

const AwaitingRbac: React.FC<{ onRecheck: () => void; rechecking: boolean }> = ({ onRecheck, rechecking }) => (
  <div className="bg-anchor-card border border-amber-400/20 rounded-xl p-12 text-center">
    <ClockIcon className="h-16 w-16 text-amber-400/40 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-white mb-2">Connected — Waiting for Role Assignment</h3>
    <p className="text-sm text-anchor-muted max-w-md mx-auto mb-6">
      Admin consent succeeded, but ANCHOR can't see any subscriptions yet.
      An Azure administrator needs to grant the ANCHOR app the <span className="text-white/80 font-medium">Reader</span> role.
    </p>
    <div className="inline-flex flex-col items-start text-left bg-white/[0.02] border border-white/[0.06] rounded-lg p-4 text-xs text-anchor-muted space-y-1.5 mb-6">
      <span className="text-white/70 font-medium">In the Azure portal:</span>
      <span>1. Open your subscription &rarr; Access control (IAM)</span>
      <span>2. Add role assignment &rarr; <span className="text-white/70">Reader</span></span>
      <span>3. Assign to the <span className="text-white/70">ANCHOR</span> enterprise application</span>
    </div>
    <div>
      <button
        onClick={onRecheck}
        disabled={rechecking}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-anchor-blue hover:bg-anchor-blue/80 transition-colors disabled:opacity-50"
      >
        <ArrowPathIcon className={`h-4 w-4 ${rechecking ? 'animate-spin' : ''}`} />
        {rechecking ? 'Checking...' : 'Check again'}
      </button>
    </div>
  </div>
);

// In-app confirmation (never window.confirm — automation and some browser
// settings silently dismiss native dialogs, making the button appear dead)
const DisconnectConfirmDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}> = ({ isOpen, onClose, onConfirm, isSubmitting }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={isSubmitting ? undefined : onClose} />
      <div className="relative bg-anchor-card border border-white/[0.08] rounded-xl p-6 w-full max-w-sm mx-4 shadow-2xl">
        <h3 className="text-lg font-medium text-white mb-2">Disconnect Azure</h3>
        <p className="text-sm text-anchor-muted mb-5">
          ANCHOR stops auditing your Azure backups and Cloud Backups returns to setup.
          Reconnecting takes one Azure administrator sign-in.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 border border-white/[0.08] rounded-lg text-sm font-medium text-anchor-light bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-red-500/80 hover:bg-red-500 transition-colors disabled:opacity-50"
          >
            {isSubmitting && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {isSubmitting ? 'Disconnecting...' : 'Disconnect'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Justification dialog for excluding a resource from the audit
const ExclusionDialog: React.FC<{
  target: { resourceKey: string; resourceName: string; resourceType: 'unprotected_vm' | 'protected_item' } | null;
  onClose: () => void;
  onConfirm: (justification: string) => void;
  isSubmitting: boolean;
}> = ({ target, onClose, onConfirm, isSubmitting }) => {
  const [justification, setJustification] = useState('');
  useEffect(() => { if (target) setJustification(''); }, [target]);
  if (!target) return null;
  const valid = justification.trim().length >= 10;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={isSubmitting ? undefined : onClose} />
      <div className="relative bg-anchor-card border border-white/[0.08] rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <h3 className="text-lg font-medium text-white mb-1">Exclude from audit</h3>
        <p className="text-sm text-anchor-muted mb-4">
          <span className="text-white/80">{target.resourceName}</span> stops counting against the backup
          score. It stays listed under "Excluded from Audit" with your justification.
        </p>
        <textarea
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          placeholder="Why is this resource intentionally not backed up? (at least 10 characters)"
          rows={3}
          className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-white/25 focus:outline-none focus:border-anchor-blue/60 resize-none"
        />
        <div className="flex gap-3 mt-4">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 border border-white/[0.08] rounded-lg text-sm font-medium text-anchor-light bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(justification.trim())}
            disabled={isSubmitting || !valid}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-anchor-blue hover:bg-anchor-blue/80 transition-colors disabled:opacity-50"
          >
            {isSubmitting && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {isSubmitting ? 'Excluding...' : 'Exclude'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConsentBanner: React.FC<{ type: 'success' | 'error'; message: string; onDismiss: () => void }> = ({ type, message, onDismiss }) => (
  <div className={`rounded-lg px-4 py-3 text-sm flex items-center justify-between gap-3 ${
    type === 'success'
      ? 'bg-green-500/10 border border-green-500/20 text-green-400'
      : 'bg-red-500/10 border border-red-500/20 text-red-400'
  }`}>
    <div className="flex items-start gap-2 min-w-0">
      {type === 'success' ? <CheckCircleIcon className="h-5 w-5 flex-shrink-0" /> : <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />}
      <span className="line-clamp-3 break-words" title={message}>{message}</span>
    </div>
    <button onClick={onDismiss} className="text-white/40 hover:text-white/60 text-xs flex-shrink-0">Dismiss</button>
  </div>
);

const CloudBackupsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [auditing, setAuditing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [configStatus, setConfigStatus] = useState<ConfigStatus | null>(null);
  const [auditResult, setAuditResult] = useState<AuditResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [consentBanner, setConsentBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [rechecking, setRechecking] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [exclusionTarget, setExclusionTarget] = useState<{
    resourceKey: string; resourceName: string; resourceType: 'unprotected_vm' | 'protected_item';
  } | null>(null);
  const [excluding, setExcluding] = useState(false);

  const checkStatus = useCallback(async (): Promise<ConfigStatus | null> => {
    try {
      const status = await api.getAzureBackupStatus();
      setConfigStatus(status);
      return status;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check Azure configuration');
      return null;
    }
  }, []);

  const runAudit = useCallback(async (forceRefresh = false) => {
    setAuditing(true);
    setError(null);
    try {
      const result = await api.getAzureBackupAudit(forceRefresh);
      setAuditResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Audit failed');
    } finally {
      setAuditing(false);
    }
  }, []);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      const { consentUrl } = await api.initiateAzureConsent('/cloud-backups');
      window.location.href = consentUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate Azure consent');
      setConnecting(false);
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    try {
      await api.revokeAzureConsent();
      setAuditResult(null);
      setConsentBanner({ type: 'success', message: 'Azure integration disconnected.' });
      await checkStatus();
      setShowDisconnectConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect Azure');
    } finally {
      setDisconnecting(false);
    }
  }, [checkStatus]);

  const handleConfirmExclusion = useCallback(async (justification: string) => {
    if (!exclusionTarget) return;
    setExcluding(true);
    setError(null);
    try {
      await api.addAuditExclusion({ ...exclusionTarget, justification });
      setExclusionTarget(null);
      await runAudit(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to exclude resource');
    } finally {
      setExcluding(false);
    }
  }, [exclusionTarget, runAudit]);

  const handleRemoveExclusion = useCallback(async (id: string) => {
    setError(null);
    try {
      await api.removeAuditExclusion(id);
      await runAudit(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove exclusion');
    }
  }, [runAudit]);

  // Re-discover subscriptions after the customer assigns the Reader role
  const handleRecheckRbac = useCallback(async () => {
    setRechecking(true);
    setError(null);
    try {
      const result = await api.refreshAzureSubscriptions();
      const status = await checkStatus();
      if (result.subscriptionCount > 0 && status?.hasSubscription) {
        setConsentBanner({
          type: 'success',
          message: `Found ${result.subscriptionCount} subscription${result.subscriptionCount !== 1 ? 's' : ''}. Running backup audit...`,
        });
        await runAudit(true);
      } else {
        setConsentBanner({
          type: 'error',
          message: 'Still no subscriptions visible. Verify the Reader role assignment has been saved — Azure can take a few minutes to propagate it.',
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh subscriptions');
    } finally {
      setRechecking(false);
    }
  }, [checkStatus, runAudit]);

  useEffect(() => {
    const consentResult = searchParams.get('consent');
    if (consentResult) {
      if (consentResult === 'success') {
        // Re-check status explicitly — consent may complete before RBAC roles exist
        checkStatus().then(status => {
          if (status?.hasSubscription) {
            setConsentBanner({ type: 'success', message: 'Azure connected successfully. Running backup audit...' });
          } else {
            setConsentBanner({ type: 'success', message: 'Azure connected. Assign the Reader role to finish setup.' });
          }
        });
      } else {
        const errMsg = searchParams.get('error') || 'Admin consent was denied or failed.';
        setConsentBanner({ type: 'error', message: `Azure consent failed: ${decodeURIComponent(errMsg)}` });
      }
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, checkStatus]);

  useEffect(() => {
    const init = async () => {
      const status = await checkStatus();
      if (status?.configured && status?.hasSubscription) {
        await runAudit();
      }
      setLoading(false);
    };
    init();
  }, [checkStatus, runAudit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-anchor-blue mx-auto" />
          <p className="mt-4 text-anchor-muted">Checking Azure backup configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-medium text-white">Cloud Backups</h2>
          <p className="text-anchor-muted mt-1">
            Azure Recovery Services &amp; Backup Center audit
          </p>
        </div>
        {configStatus?.configured && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowDisconnectConfirm(true)}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-medium text-white/50 hover:text-red-400 hover:bg-red-400/5 border border-white/[0.06] transition-colors"
            >
              Disconnect
            </button>
            {configStatus.hasSubscription && (
              <button
                onClick={() => runAudit(true)}
                disabled={auditing}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-anchor-blue hover:bg-anchor-blue/80 transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 ${auditing ? 'animate-spin' : ''}`} />
                {auditing ? 'Auditing...' : 'Run Audit'}
              </button>
            )}
          </div>
        )}
      </div>

      <DisconnectConfirmDialog
        isOpen={showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(false)}
        onConfirm={handleDisconnect}
        isSubmitting={disconnecting}
      />

      <ExclusionDialog
        target={exclusionTarget}
        onClose={() => setExclusionTarget(null)}
        onConfirm={handleConfirmExclusion}
        isSubmitting={excluding}
      />

      {consentBanner && (
        <ConsentBanner
          type={consentBanner.type}
          message={consentBanner.message}
          onDismiss={() => setConsentBanner(null)}
        />
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {!configStatus?.configured && <NotConfigured onConnect={handleConnect} connecting={connecting} />}

      {configStatus?.configured && !configStatus.hasSubscription && (
        <AwaitingRbac onRecheck={handleRecheckRbac} rechecking={rechecking} />
      )}

      {/* Partial-audit warning: some scopes could not be assessed */}
      {auditResult && auditResult.details.auditGaps && auditResult.details.auditGaps.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3 text-sm text-amber-400">
          <div className="flex items-center gap-2 font-medium mb-1.5">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            {auditResult.details.auditGaps.length} scope{auditResult.details.auditGaps.length !== 1 ? 's' : ''} could not be audited — the score reflects only what was assessed
          </div>
          <ul className="ml-7 space-y-0.5 text-amber-300/80 text-xs">
            {auditResult.details.auditGaps.map((gap, i) => (
              <li key={i}>
                <span className="uppercase tracking-wider text-[10px] mr-1.5">{gap.scope}</span>
                {gap.resource} — {gap.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {configStatus?.configured && configStatus.hasSubscription && auditResult && (
        <>
          {/* Score hero */}
          <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-6">
            <div className="flex items-center gap-8">
              <ScoreRing score={auditResult.score} status={auditResult.status} />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-lg font-medium text-white">Backup Posture</h3>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    statusColors[auditResult.status]?.bg} ${statusColors[auditResult.status]?.text}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${statusColors[auditResult.status]?.dot}`} />
                    {auditResult.status === 'pass' ? 'Healthy' : auditResult.status === 'warn' ? 'Needs Attention' : 'Critical'}
                  </span>
                </div>
                <p className="text-sm text-anchor-muted mb-4">{auditResult.details.summary}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-white/[0.02] rounded-lg px-3 py-2">
                    <div className="text-xl font-bold text-white">{auditResult.details.vaults.length}</div>
                    <div className="text-[11px] text-anchor-muted">Vaults</div>
                  </div>
                  <div className="bg-white/[0.02] rounded-lg px-3 py-2">
                    <div className="text-xl font-bold text-white">{auditResult.details.protectedItems.length}</div>
                    <div className="text-[11px] text-anchor-muted">Protected Items</div>
                  </div>
                  <div className="bg-white/[0.02] rounded-lg px-3 py-2">
                    <div className="text-xl font-bold text-red-400">{auditResult.details.unprotectedVMs.length}</div>
                    <div className="text-[11px] text-anchor-muted">Unprotected VMs</div>
                  </div>
                  <div className="bg-white/[0.02] rounded-lg px-3 py-2">
                    <div className="text-xl font-bold text-amber-400">{auditResult.findings}</div>
                    <div className="text-[11px] text-anchor-muted">Findings</div>
                  </div>
                </div>
              </div>
            </div>
            {auditResult.timestamp && (
              <div className="mt-4 pt-3 border-t border-white/[0.04] flex items-center gap-1.5 text-[11px] text-anchor-muted">
                <ClockIcon className="h-3 w-3" />
                Last audit: {new Date(auditResult.timestamp).toLocaleString()}
                {configStatus.subscriptionId && (
                  <span className="ml-3">Subscription: {configStatus.subscriptionId}</span>
                )}
              </div>
            )}
          </div>

          {/* Vault Security Posture */}
          {auditResult.details.vaultSecurity && auditResult.details.vaultSecurity.length > 0 && (
            <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-white/[0.06] flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5 text-anchor-blue" />
                <h3 className="text-base font-medium text-white">Vault Security Posture</h3>
              </div>
              <div className="divide-y divide-white/[0.04]">
                {auditResult.details.vaultSecurity.map((vault, i) => (
                  <div key={i} className="px-6 py-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ServerStackIcon className="h-4 w-4 text-anchor-muted" />
                        <span className="text-sm font-medium text-white">{vault.vaultName}</span>
                      </div>
                      {vault.issues.length === 0 ? (
                        <span className="text-xs text-green-400">All checks passed</span>
                      ) : (
                        <span className="text-xs text-amber-400">{vault.issues.length} issue{vault.issues.length !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <SecurityBadge enabled={vault.softDeleteEnabled} label="Soft Delete" />
                      <SecurityBadge enabled={vault.immutabilityEnabled} label="Immutability" />
                      <SecurityBadge enabled={vault.enhancedSecurityEnabled} label="Enhanced Security" />
                      <SecurityBadge enabled={vault.geoRedundant} label="Geo-Redundant" />
                      <SecurityBadge enabled={vault.crossRegionRestoreEnabled} label="Cross-Region Restore" />
                      <SecurityBadge enabled={vault.publicNetworkDisabled} label="Private Access" />
                      <SecurityBadge enabled={vault.customerManagedKey} label="CMK Encryption" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vaults list */}
          {auditResult.details.vaults.length > 0 && (
            <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-white/[0.06] flex items-center gap-2">
                <ServerStackIcon className="h-5 w-5 text-anchor-blue" />
                <h3 className="text-base font-medium text-white">Recovery Vaults</h3>
                <span className="text-xs text-anchor-muted ml-auto">{auditResult.details.vaults.length} vault{auditResult.details.vaults.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-white/[0.06]">
                  <thead>
                    <tr className="bg-white/[0.02]">
                      <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Vault</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Resource Group</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {auditResult.details.vaults.map((vault, i) => (
                      <tr key={i} className="hover:bg-white/[0.02]">
                        <td className="px-6 py-3 text-sm text-white">{vault.name}</td>
                        <td className="px-6 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                            vault.type === 'DataProtection' ? 'bg-purple-400/10 text-purple-400' : 'bg-anchor-blue/10 text-anchor-blue'
                          }`}>
                            {vault.type === 'DataProtection' ? 'Backup Center' : 'Recovery Services'}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-anchor-muted">{vault.location}</td>
                        <td className="px-6 py-3 text-sm text-anchor-muted">{vault.resourceGroup}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Protected Items */}
          {auditResult.details.protectedItems.length > 0 && (
            <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-white/[0.06]">
                <h3 className="text-base font-medium text-white">Protected Items</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-white/[0.06]">
                  <thead>
                    <tr className="bg-white/[0.02]">
                      <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Item</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Vault</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Last Backup</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Health</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {auditResult.details.protectedItems.slice(0, 50).map((item, i) => {
                      const itemName = item.name.split(';').pop() || item.name;
                      const healthOk = item.healthStatus?.toLowerCase() === 'healthy';
                      const backupTime = item.lastBackupTime || item.lastRecoveryPoint;
                      return (
                        <tr key={i} className="hover:bg-white/[0.02]">
                          <td className="px-6 py-3 text-sm text-white truncate max-w-[200px]" title={item.name}>{itemName}</td>
                          <td className="px-6 py-3 text-xs text-anchor-muted">{item.resourceType}</td>
                          <td className="px-6 py-3 text-xs text-anchor-muted">{item.vaultName}</td>
                          <td className="px-6 py-3 text-xs text-anchor-muted">
                            {backupTime ? new Date(backupTime).toLocaleDateString() : <span className="text-red-400">Never</span>}
                          </td>
                          <td className="px-6 py-3">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                              healthOk ? 'bg-green-400/10 text-green-400' : 'bg-amber-400/10 text-amber-400'
                            }`}>
                              {item.healthStatus}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {auditResult.details.protectedItems.length > 50 && (
                  <div className="px-6 py-3 text-xs text-anchor-muted border-t border-white/[0.04]">
                    Showing 50 of {auditResult.details.protectedItems.length} items
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Unprotected VMs */}
          {auditResult.details.unprotectedVMs.length > 0 && (
            <div className="bg-anchor-card border border-red-500/20 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-white/[0.06] flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                <h3 className="text-base font-medium text-white">Unprotected Virtual Machines</h3>
                <span className="text-xs text-red-400 ml-auto">{auditResult.details.unprotectedVMs.length} VM{auditResult.details.unprotectedVMs.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="px-6 py-4">
                <div className="flex flex-wrap gap-2">
                  {auditResult.details.unprotectedVMs.map((vm, i) => (
                    <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-400/5 border border-red-400/20 rounded-lg text-xs text-red-300">
                      {vm.name}
                      <button
                        onClick={() => setExclusionTarget({ resourceKey: vm.id, resourceName: vm.name, resourceType: 'unprotected_vm' })}
                        className="text-white/30 hover:text-white/70 transition-colors"
                        title="Exclude from audit (requires justification)"
                      >
                        Exclude
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Excluded from audit */}
          {auditResult.details.excluded && auditResult.details.excluded.length > 0 && (
            <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-white/[0.06] flex items-center gap-2">
                <h3 className="text-base font-medium text-white/70">Excluded from Audit</h3>
                <span className="text-xs text-anchor-muted ml-auto">
                  {auditResult.details.excluded.length} item{auditResult.details.excluded.length !== 1 ? 's' : ''} — not counted in the score
                </span>
              </div>
              <div className="divide-y divide-white/[0.04]">
                {auditResult.details.excluded.map((ex) => (
                  <div key={ex.id} className="px-6 py-3 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm text-white/80">{ex.resourceName}
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-anchor-muted">
                          {ex.resourceType === 'unprotected_vm' ? 'VM' : 'Protected item'}
                        </span>
                      </div>
                      <div className="text-xs text-anchor-muted mt-0.5 line-clamp-2" title={ex.justification}>
                        {ex.justification}
                      </div>
                      {ex.createdBy && (
                        <div className="text-[10px] text-white/30 mt-0.5">by {ex.createdBy}</div>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveExclusion(ex.id)}
                      className="text-xs text-white/40 hover:text-anchor-blue transition-colors flex-shrink-0"
                    >
                      Include again
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scoring Breakdown */}
          {auditResult.details.scoringBreakdown && (
            <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-6">
              <h3 className="text-base font-medium text-white mb-4">Scoring Breakdown</h3>
              <div className="space-y-2">
                {Object.entries(auditResult.details.scoringBreakdown)
                  .filter(([key, val]) => key !== 'totalDeductions' && (val as number) > 0)
                  .map(([key, val]) => {
                    const labels: Record<string, string> = {
                      noVault: 'No vaults found',
                      noProtectedItems: 'No protected items',
                      unprotectedResources: 'Unprotected resources',
                      failedBackups: 'Failed backups',
                      staleBackups: 'Stale backups',
                      unhealthyItems: 'Unhealthy items',
                      softDeleteDisabled: 'Soft delete disabled',
                      noImmutability: 'No immutability',
                      noGeoRedundancy: 'No geo-redundancy',
                      noRestoreTests: 'No restore tests (90 days)',
                      publicNetworkAccess: 'Public network access',
                    };
                    return (
                      <div key={key} className="flex items-center justify-between py-1.5">
                        <span className="text-sm text-anchor-muted">{labels[key] || key}</span>
                        <span className="text-sm font-medium text-red-400">-{val as number}</span>
                      </div>
                    );
                  })
                }
                <div className="flex items-center justify-between pt-3 mt-2 border-t border-white/[0.06]">
                  <span className="text-sm font-medium text-white">Final Score</span>
                  <span className={`text-lg font-bold ${statusColors[auditResult.status]?.text}`}>{auditResult.score}/100</span>
                </div>
              </div>
            </div>
          )}

          {/* Restore Tests */}
          <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-6">
            <div className="flex items-center gap-3">
              {auditResult.details.restoreTestsFound ? (
                <>
                  <CheckCircleIcon className="h-8 w-8 text-green-400" />
                  <div>
                    <h3 className="text-sm font-medium text-white">Restore Tests Detected</h3>
                    <p className="text-xs text-anchor-muted">
                      {auditResult.details.recentRestoreJobCount} restore job{auditResult.details.recentRestoreJobCount !== 1 ? 's' : ''} found in the last 90 days
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <ExclamationTriangleIcon className="h-8 w-8 text-amber-400" />
                  <div>
                    <h3 className="text-sm font-medium text-white">No Restore Tests Found</h3>
                    <p className="text-xs text-anchor-muted">
                      No restore operations detected in the last 90 days. CIS 11.5 recommends periodic restore testing.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Automated restore testing */}
          <RestoreTestingCard />
        </>
      )}
    </div>
  );
};

// Self-contained card: restore-test config, manual trigger, and run history
const RestoreTestingCard: React.FC = () => {
  const [cfg, setCfg] = useState<{
    enabled: boolean;
    frequencyDays: number;
    stagingResourceGroup: string;
    testType: 'disk' | 'full';
    maxLifetimeMinutes: number;
    itemId?: string;
  } | null>(null);
  const [runs, setRuns] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [config, history] = await Promise.all([api.getRestoreTestConfig(), api.getRestoreTests()]);
      setCfg(config || { enabled: false, frequencyDays: 30, stagingResourceGroup: '', testType: 'disk', maxLifetimeMinutes: 30 });
      setRuns(history || []);
    } catch { /* card stays in defaults */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setBusy('save'); setMessage(null);
    try {
      await api.saveRestoreTestConfig(cfg);
      setMessage({ type: 'ok', text: 'Restore test settings saved.' });
    } catch (err) {
      setMessage({ type: 'err', text: err instanceof Error ? err.message : 'Save failed' });
    } finally { setBusy(null); }
  };

  const runNow = async (testType: 'disk' | 'full') => {
    setBusy(testType === 'full' ? 'run-full' : 'run'); setMessage(null);
    try {
      const { itemName } = await api.triggerRestoreTest(testType);
      setMessage({ type: 'ok', text: testType === 'full'
        ? `Full-VM restore test started for ${itemName} — a VM boots in an isolated resource group, its guest agent is verified, then everything is torn down.`
        : `Disk restore test started for ${itemName} — disks restore into the staging group and are cleaned up automatically.` });
      await load();
    } catch (err) {
      setMessage({ type: 'err', text: err instanceof Error ? err.message : 'Restore test failed to start' });
    } finally { setBusy(null); }
  };

  if (!cfg) return null;

  return (
    <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-white/[0.06] flex items-center gap-2">
        <ArrowPathIcon className="h-5 w-5 text-anchor-blue" />
        <h3 className="text-base font-medium text-white">Automated Restore Testing</h3>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => runNow('disk')}
            disabled={busy !== null || !cfg.stagingResourceGroup}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-anchor-blue hover:bg-anchor-blue/80 transition-colors disabled:opacity-40"
            title={!cfg.stagingResourceGroup ? 'Set a staging resource group first' : undefined}
          >
            {busy === 'run' && <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />}
            {busy === 'run' ? 'Starting...' : 'Disk test'}
          </button>
          <button
            onClick={() => runNow('full')}
            disabled={busy !== null || !cfg.stagingResourceGroup}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-purple-500/80 hover:bg-purple-500 transition-colors disabled:opacity-40"
            title={!cfg.stagingResourceGroup ? 'Set a staging resource group first' : 'Boots a full VM in isolation and verifies its guest agent'}
          >
            {busy === 'run-full' && <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />}
            {busy === 'run-full' ? 'Starting...' : 'Full VM test'}
          </button>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        <p className="text-xs text-anchor-muted">
          <span className="text-white/70">Disk test</span> restores the latest recovery point's disks into the staging
          resource group and deletes them — proving the backup is restorable. <span className="text-white/70">Full VM test</span> goes
          further: it boots a VM from the backup in a per-run, network-isolated resource group, confirms the guest
          agent reports healthy, then tears the whole group down. Full tests need
          <span className="text-white/70"> Backup Operator</span> (vault),
          <span className="text-white/70"> VM Contributor</span> (source VM), and
          <span className="text-white/70"> Contributor</span> (staging group) on the ANCHOR app.
        </p>

        <div className="flex flex-wrap items-end gap-4">
          <label className="flex items-center gap-2 text-sm text-white/80 pb-2">
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              className="accent-anchor-blue"
            />
            Scheduled testing
          </label>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-anchor-muted mb-1">Every (days)</div>
            <input
              type="number" min={1} max={90}
              value={cfg.frequencyDays}
              onChange={(e) => setCfg({ ...cfg, frequencyDays: Number(e.target.value) })}
              className="w-24 bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-anchor-blue/60"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-anchor-muted mb-1">Scheduled test type</div>
            <select
              value={cfg.testType}
              onChange={(e) => setCfg({ ...cfg, testType: e.target.value as 'disk' | 'full' })}
              className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-anchor-blue/60"
            >
              <option value="disk">Disk restore</option>
              <option value="full">Full VM + heartbeat</option>
            </select>
          </div>
          {cfg.testType === 'full' && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-anchor-muted mb-1">Max lifetime (min)</div>
              <input
                type="number" min={21} max={240}
                value={cfg.maxLifetimeMinutes}
                onChange={(e) => setCfg({ ...cfg, maxLifetimeMinutes: Number(e.target.value) })}
                className="w-24 bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-anchor-blue/60"
              />
            </div>
          )}
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-wider text-anchor-muted mb-1">Staging resource group (ANCHOR-managed scratch space)</div>
            <input
              type="text"
              value={cfg.stagingResourceGroup}
              onChange={(e) => setCfg({ ...cfg, stagingResourceGroup: e.target.value })}
              placeholder="anchor-restore-tests"
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-white/25 focus:outline-none focus:border-anchor-blue/60"
            />
          </div>
          <button
            onClick={save}
            disabled={busy !== null}
            className="px-4 py-2 rounded-lg text-sm font-medium text-anchor-light border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-50"
          >
            {busy === 'save' ? 'Saving...' : 'Save'}
          </button>
        </div>

        {message && (
          <div className={`text-xs ${message.type === 'ok' ? 'text-green-400' : 'text-red-400'}`}>{message.text}</div>
        )}

        {runs.length > 0 && (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-anchor-muted uppercase tracking-wider text-[10px]">
                <th className="py-1.5 pr-4">Item</th>
                <th className="py-1.5 pr-4">Status</th>
                <th className="py-1.5 pr-4">Trigger</th>
                <th className="py-1.5 pr-4">Started</th>
                <th className="py-1.5">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {runs.map((r) => (
                <tr key={r.id}>
                  <td className="py-1.5 pr-4 text-white/80">{r.itemName || '—'}</td>
                  <td className="py-1.5 pr-4">
                    <span className={
                      r.status === 'completed' ? 'text-green-400'
                        : r.status === 'failed' ? 'text-red-400'
                        : 'text-amber-400'
                    }>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-1.5 pr-4 text-anchor-muted">{r.triggeredBy}</td>
                  <td className="py-1.5 pr-4 text-anchor-muted">{new Date(r.startedAt).toLocaleString()}</td>
                  <td className="py-1.5 text-anchor-muted line-clamp-1" title={r.error || ''}>{r.error || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CloudBackupsPage;
